import React, { useState, useEffect, useRef, useCallback } from 'react';
import type { LearningProgress } from './useLearningProgress';

interface LearningCard {
  id: string;
  label: string;
  title: string;
  body: string;
  keyPoint: string;
  icon: string;
  color: string;
}

interface QuizOption {
  id: string;
  text: string;
  correct: boolean;
}

interface LearningQuiz {
  question: string;
  options: QuizOption[];
  explanation: string;
}

interface LearningModuleData {
  title: string;
  subtitle: string;
  estimatedTime: string;
  cards: LearningCard[];
  quiz: LearningQuiz;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  module: LearningModuleData;
  progress: LearningProgress;
  onMarkCardComplete: (cardId: string) => void;
  onSetStep: (step: number) => void;
  onCompleteQuiz: (correct: boolean) => void;
  onReset: () => void;
}

type SlideDirection = 'left' | 'right' | null;

const LearningModuleModal: React.FC<Props> = ({
  isOpen,
  onClose,
  module,
  progress,
  onMarkCardComplete,
  onSetStep,
  onCompleteQuiz,
  onReset
}) => {
  const totalCards = module.cards.length;
  const totalSteps = totalCards + 1; // cards + quiz
  const [step, setStepLocal] = useState(progress.currentStep);
  const [slideDir, setSlideDir] = useState<SlideDirection>(null);
  const [animating, setAnimating] = useState(false);
  const [showCompletion, setShowCompletion] = useState(progress.quizCompleted);

  useEffect(() => {
    if (isOpen) {
      if (progress.quizCompleted) {
        setShowCompletion(true);
      } else {
        setStepLocal(progress.currentStep);
        setShowCompletion(false);
      }
    }
  }, [isOpen, progress.currentStep, progress.quizCompleted]);

  // Lock body scroll
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isOpen]);

  const animateTo = useCallback(
    (nextStep: number, dir: SlideDirection) => {
      if (animating) return;
      setSlideDir(dir);
      setAnimating(true);
      setTimeout(() => {
        setStepLocal(nextStep);
        onSetStep(nextStep);
        setSlideDir(null);
        setAnimating(false);
      }, 200);
    },
    [animating, onSetStep]
  );

  const goNext = () => {
    if (step < totalCards) {
      const card = module.cards[step];
      if (card) onMarkCardComplete(card.id);
    }
    if (step < totalSteps - 1) {
      animateTo(step + 1, 'left');
    }
  };

  const goBack = () => {
    if (step > 0) animateTo(step - 1, 'right');
  };

  const handleQuizComplete = (correct: boolean) => {
    onCompleteQuiz(correct);
    setTimeout(() => setShowCompletion(true), 2500);
  };

  const handleReviewAgain = () => {
    onReset();
    setShowCompletion(false);
    setStepLocal(0);
  };

  if (!isOpen) return null;

  const isOnQuiz = step === totalCards;
  const isCompleted = showCompletion;
  const _headerIcon = isCompleted ? '✓' : '📖';
  const headerTitle = isCompleted ? "You're ready" : 'Before You Dive In';
  const headerSubtitle = isCompleted
    ? 'Background complete'
    : `${totalCards} things that will help you understand every perspective · ${module.estimatedTime}`;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[9999] flex items-center justify-center p-6"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />

        {/* Modal */}
        <div
          className="relative bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden"
          style={{
            width: 'min(540px, calc(100vw - 48px))',
            maxHeight: 'calc(100vh - 80px)'
          }}
        >
          {/* ── Header ─── */}
          <div className="shrink-0 px-6 pt-5 pb-4 border-b border-gray-100">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-[30px] h-[30px] rounded-lg flex items-center justify-center text-sm"
                  style={{
                    background: isCompleted
                      ? 'linear-gradient(135deg, #34d399, #059669)'
                      : 'linear-gradient(135deg, #fbbf24, #f59e0b)'
                  }}
                >
                  <span className="text-white text-xs font-bold">{isCompleted ? '✓' : '📖'}</span>
                </div>
                <div>
                  <h2
                    className="text-[16px] font-bold text-slate-900"
                    style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
                  >
                    {headerTitle}
                  </h2>
                  <p className="text-[12px] text-slate-500 mt-0.5">{headerSubtitle}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors text-slate-400 hover:text-slate-600"
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
              </button>
            </div>

            {/* Progress dots */}
            {!isCompleted && (
              <div className="flex items-center gap-1.5 mt-3">
                {module.cards.map((_, i) => {
                  const isCurrent = i === step && !isOnQuiz;
                  const isDone = progress.completedCards.includes(module.cards[i].id);
                  return (
                    <div
                      key={i}
                      className="h-[6px] rounded-full transition-all duration-300"
                      style={{
                        width: isCurrent ? 24 : 6,
                        backgroundColor: isDone ? '#059669' : isCurrent ? '#1e293b' : '#e2e8f0'
                      }}
                    />
                  );
                })}
                {/* Quiz dot */}
                <div
                  className="h-[6px] rounded-full transition-all duration-300"
                  style={{
                    width: isOnQuiz ? 24 : 6,
                    backgroundColor: progress.quizCompleted
                      ? '#059669'
                      : isOnQuiz
                        ? '#1e293b'
                        : '#e2e8f0'
                  }}
                />
              </div>
            )}
          </div>

          {/* ── Body ─── */}
          <div className="flex-1 overflow-y-auto px-6 py-5">
            <div
              className="transition-all duration-200 ease-out"
              style={{
                opacity: animating ? 0 : 1,
                transform: animating
                  ? slideDir === 'left'
                    ? 'translateX(-20px)'
                    : 'translateX(20px)'
                  : 'translateX(0)'
              }}
            >
              {isCompleted ? (
                <CompletionView onClose={onClose} onReviewAgain={handleReviewAgain} />
              ) : isOnQuiz ? (
                <QuizView
                  quiz={module.quiz}
                  alreadyCompleted={progress.quizCompleted}
                  onComplete={handleQuizComplete}
                />
              ) : (
                <CardView card={module.cards[step]} />
              )}
            </div>
          </div>

          {/* ── Footer ─── */}
          {!isCompleted && (
            <div className="shrink-0 px-6 py-4 border-t border-gray-100 flex items-center justify-between">
              <button
                onClick={goBack}
                disabled={step === 0}
                className="inline-flex items-center gap-1 text-[13px] font-medium text-slate-600 hover:text-slate-900 disabled:opacity-30 disabled:cursor-not-allowed transition-colors rounded-lg border border-gray-200 px-3 py-2 hover:bg-gray-50"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <polyline points="15 18 9 12 15 6" />
                </svg>
                Back
              </button>

              <span className="text-[12px] text-slate-400">
                {isOnQuiz ? 'Quick check' : `${step + 1} of ${totalCards}`}
              </span>

              {!isOnQuiz && (
                <button
                  onClick={goNext}
                  className="inline-flex items-center gap-1 text-[13px] font-bold text-white bg-slate-900 hover:bg-slate-800 px-4 py-2 rounded-lg transition-colors"
                >
                  {step === totalCards - 1 ? 'Take the quiz' : 'Next'}
                  <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="9 18 15 12 9 6" />
                  </svg>
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ─── Card View ───────────────────────────────────────────────────────────

const CardView: React.FC<{ card: LearningCard }> = ({ card }) => {
  const [showKeyPoint, setShowKeyPoint] = useState(false);

  return (
    <div>
      {/* Label + icon */}
      <div className="flex items-center gap-2.5 mb-4">
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center text-lg"
          style={{ backgroundColor: card.color + '15' }}
        >
          {card.icon}
        </div>
        <span
          className="text-[10px] font-bold uppercase tracking-[0.1em]"
          style={{ color: card.color }}
        >
          {card.label}
        </span>
      </div>

      {/* Title */}
      <h3
        className="text-[22px] font-bold text-slate-900 leading-tight mb-4"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {card.title}
      </h3>

      {/* Body */}
      <p
        className="text-[15px] text-[#475569] leading-[1.72] mb-5"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {card.body}
      </p>

      {/* Why this matters reveal */}
      {!showKeyPoint ? (
        <button
          onClick={() => setShowKeyPoint(true)}
          className="w-full rounded-xl border-2 border-dashed border-amber-300 px-4 py-3 flex items-center gap-2.5 text-left hover:border-amber-400 hover:bg-amber-50/50 transition-all"
        >
          <span className="text-amber-500 text-base">⚡</span>
          <span className="text-[13px] font-semibold text-amber-700">
            Why this matters for the debate →
          </span>
        </button>
      ) : (
        <div
          className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3.5 animate-fadeIn"
          style={{ animation: 'fadeIn 0.3s ease-out' }}
        >
          <div className="flex items-center gap-2 mb-1.5">
            <span className="text-amber-500 text-sm">⚡</span>
            <span className="text-[10px] font-bold uppercase tracking-wider text-amber-600">
              Why this matters
            </span>
          </div>
          <p
            className="text-[14px] text-amber-900 leading-relaxed"
            style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
          >
            {card.keyPoint}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Quiz View ───────────────────────────────────────────────────────────

const QuizView: React.FC<{
  quiz: LearningQuiz;
  alreadyCompleted: boolean;
  onComplete: (correct: boolean) => void;
}> = ({ quiz, alreadyCompleted, onComplete }) => {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(alreadyCompleted);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleSubmit = () => {
    if (!selectedId) return;
    const correct = quiz.options.find((o) => o.id === selectedId)?.correct ?? false;
    setIsCorrect(correct);
    setSubmitted(true);
    onComplete(correct);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-4">
        <div className="w-9 h-9 rounded-xl bg-indigo-50 flex items-center justify-center">
          <span className="text-indigo-600 text-lg">?</span>
        </div>
        <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-indigo-600">
          Quick check
        </span>
      </div>

      <h3
        className="text-[18px] font-bold text-slate-900 leading-snug mb-5"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        {quiz.question}
      </h3>

      <div className="flex flex-col gap-2.5 mb-5">
        {quiz.options.map((opt) => {
          let borderColor = '#e2e8f0';
          let bgColor = 'white';
          let textColor = '#334155';
          let indicator = 'empty';

          if (submitted) {
            if (opt.correct) {
              borderColor = '#059669';
              bgColor = '#ecfdf5';
              textColor = '#065f46';
              indicator = 'correct';
            } else if (opt.id === selectedId && !opt.correct) {
              borderColor = '#dc2626';
              bgColor = '#fef2f2';
              textColor = '#991b1b';
              indicator = 'wrong';
            }
          } else if (opt.id === selectedId) {
            borderColor = '#1e293b';
            bgColor = '#f8fafc';
            indicator = 'selected';
          }

          return (
            <button
              key={opt.id}
              onClick={() => !submitted && setSelectedId(opt.id)}
              disabled={submitted}
              className="w-full rounded-xl border-2 px-4 py-3.5 flex items-center gap-3 text-left transition-all"
              style={{ borderColor, backgroundColor: bgColor }}
            >
              {/* Radio / result indicator */}
              <div
                className="w-5 h-5 rounded-full border-2 flex items-center justify-center shrink-0"
                style={{
                  borderColor:
                    indicator === 'correct'
                      ? '#059669'
                      : indicator === 'wrong'
                        ? '#dc2626'
                        : indicator === 'selected'
                          ? '#1e293b'
                          : '#cbd5e1',
                  backgroundColor:
                    indicator === 'correct'
                      ? '#059669'
                      : indicator === 'wrong'
                        ? '#dc2626'
                        : indicator === 'selected'
                          ? '#1e293b'
                          : 'transparent'
                }}
              >
                {indicator === 'correct' && (
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                )}
                {indicator === 'wrong' && (
                  <svg
                    width="10"
                    height="10"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="white"
                    strokeWidth="3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                )}
                {indicator === 'selected' && <div className="w-2 h-2 rounded-full bg-white" />}
              </div>

              <span className="text-[14px] leading-snug" style={{ color: textColor }}>
                {opt.text}
              </span>
            </button>
          );
        })}
      </div>

      {!submitted ? (
        <button
          onClick={handleSubmit}
          disabled={!selectedId}
          className="w-full rounded-xl bg-slate-900 text-white font-bold text-[14px] py-3 hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
        >
          Check answer
        </button>
      ) : (
        <div
          className="rounded-xl border px-4 py-4"
          style={{
            borderColor: isCorrect ? '#a7f3d0' : '#fde68a',
            backgroundColor: isCorrect ? '#ecfdf5' : '#fffbeb',
            animation: 'fadeIn 0.3s ease-out'
          }}
        >
          <p
            className="text-[12px] font-bold uppercase tracking-wider mb-1.5"
            style={{ color: isCorrect ? '#059669' : '#d97706' }}
          >
            {isCorrect ? '✓ Correct' : 'Not quite — but now you know'}
          </p>
          <p
            className="text-[14px] leading-relaxed"
            style={{
              color: isCorrect ? '#065f46' : '#92400e',
              fontFamily: "'Source Serif 4', Georgia, serif"
            }}
          >
            {quiz.explanation}
          </p>
        </div>
      )}
    </div>
  );
};

// ─── Completion View ─────────────────────────────────────────────────────

const CompletionView: React.FC<{
  onClose: () => void;
  onReviewAgain: () => void;
}> = ({ onClose, onReviewAgain }) => {
  return (
    <div className="flex flex-col items-center text-center py-8">
      {/* Checkmark circle */}
      <div
        className="w-20 h-20 rounded-full flex items-center justify-center mb-6"
        style={{ background: 'linear-gradient(135deg, #34d399, #059669)' }}
      >
        <svg
          width="36"
          height="36"
          viewBox="0 0 24 24"
          fill="none"
          stroke="white"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </div>

      <h2
        className="text-[22px] font-bold text-slate-900 mb-2"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        You've got the context
      </h2>
      <p
        className="text-[14px] text-slate-500 leading-relaxed max-w-[360px] mb-8"
        style={{ fontFamily: "'Source Serif 4', Georgia, serif" }}
      >
        You now have the background to evaluate each perspective on its merits — not just how it
        sounds.
      </p>

      <div className="flex flex-col gap-3 w-full max-w-[280px]">
        <button
          onClick={onClose}
          className="w-full rounded-xl bg-emerald-600 text-white font-bold text-[14px] py-3 hover:bg-emerald-700 transition-colors flex items-center justify-center gap-2"
        >
          Read the perspectives
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <polyline points="9 18 15 12 9 6" />
          </svg>
        </button>
        <button
          onClick={onReviewAgain}
          className="w-full rounded-xl border-2 border-gray-200 text-slate-600 font-semibold text-[13px] py-2.5 hover:bg-gray-50 transition-colors"
        >
          Review again
        </button>
      </div>
    </div>
  );
};

export default LearningModuleModal;
